import Phaser from 'phaser';
import {
  effectEnd,
  activeEffects,
  formatRemaining,
  EFFECT_ICON,
  type EffectRecord,
} from '../../config/effects';
import { hubProgress } from '../hubShared';

/**
 * EffectTimerScene — the corner timers, drawn INSIDE the game canvas so they
 * survive fullscreen (a DOM overlay sits outside the fullscreen element).
 *
 * One black square per running effect (the Diamond and the four potions):
 * icon above, m:ss below, newest stacking leftwards. Launched once at boot
 * and kept on top of every other scene; it never handles input, so clicks
 * pass straight through to whatever is underneath.
 *
 * Placement: the bottom-right corner of the canvas on every menu. During a
 * level (HudScene active) the HUD panel owns the bottom-right, so the squares
 * sit at the top-right of the playfield instead — under the top bar, left of
 * the panel — where no level puts a plot.
 */

const BOX_W = 56;
const BOX_H = 64;
const GAP = 6;
const MARGIN = 10;
const CANVAS_W = 1280;
const CANVAS_H = 720;
/** HudScene's panel starts here; the playfield's top bar ends at 50. */
const PANEL_X = 1070;
const TOP_BAR_H = 50;

export class EffectTimerScene extends Phaser.Scene {
  private layer?: Phaser.GameObjects.Container;
  private lastKey = '';
  private lastRead = 0;
  private effects: EffectRecord[] = [];

  constructor() {
    super({ key: 'EffectTimerScene' });
  }

  create(): void {
    this.layer = this.add.container(0, 0).setDepth(1000);
    this.lastKey = '';
    this.lastRead = 0;
    this.scene.bringToTop();
  }

  override update(): void {
    const now = Date.now();
    // Re-read storage on a slow clock; the countdown text ticks every second.
    if (now - this.lastRead >= 500) {
      this.lastRead = now;
      try {
        this.effects = activeEffects(hubProgress().effects(), now);
      } catch {
        this.effects = [];
      }
    }
    const inLevel = this.scene.isActive('HudScene');
    const key =
      `${inLevel ? 'L' : 'M'}|` +
      this.effects.map((e) => `${e.kind}:${formatRemaining(effectEnd(e, now) - now)}`).join(',');
    if (key === this.lastKey) return;
    this.lastKey = key;
    this.redraw(now, inLevel);
    // Keep on top: a scene started after us would otherwise render over us.
    this.scene.bringToTop();
  }

  private redraw(now: number, inLevel: boolean): void {
    const layer = this.layer;
    if (!layer) return;
    layer.removeAll(true);
    const right = inLevel ? PANEL_X - MARGIN : CANVAS_W - MARGIN;
    const bottom = inLevel ? TOP_BAR_H + MARGIN + BOX_H : CANVAS_H - MARGIN;
    this.effects.forEach((e, i) => {
      const cx = right - BOX_W / 2 - i * (BOX_W + GAP);
      const cy = bottom - BOX_H / 2;
      const box = this.add
        .rectangle(cx, cy, BOX_W, BOX_H, 0x000000, 0.9)
        .setStrokeStyle(1, e.kind === 'diamond' ? 0xffe94a : 0x3a3f4b, 1)
        .setName(`effect-timer-${e.kind}`);
      layer.add(box);
      const icon = EFFECT_ICON[e.kind];
      if (this.textures.exists(icon)) {
        layer.add(this.add.image(cx, cy - 12, icon).setDisplaySize(28, 28));
      }
      layer.add(
        this.add
          .text(cx, cy + 18, formatRemaining(effectEnd(e, now) - now), {
            fontSize: '13px',
            color: '#ffffff',
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
    });
  }
}
